import React, { useCallback, useMemo } from "react";
import { Paperclip } from "lucide-react";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import GlobalDirectoryGridSection from "../../../../../global/directory/GlobalDirectoryGridSection";
import { GlobalDirectoryPaginationSection } from "../../../../../global/directory";
import GlobalLabelFilters from "../../../../../global/GlobalLabelFilters";

/** Cycles built-in pill styles; labels/keys come only from `masters.maintenanceStatuses`. */
const STATUS_PILL_VARIANT_CYCLE = ["upcoming", "2d", "1d", "ongoing", "completed"];

const TRACKER_HEADERS = [
  { key: "type", label: "Type" },
  { key: "area", label: "Area" },
  { key: "assigned_to", label: "Assigned to" },
  { key: "status", label: "Status" },
  { key: "start_date", label: "Start" },
  { key: "completion_date", label: "Completion" },
  { key: "attachment", label: "Attachment" },
  { key: "actions", label: "Actions", className: "ub-table__col-actions" },
];

function trackerStatusBadgeClass(statusId, statusLabel) {
  const id = Number(statusId);
  const n = String(statusLabel || "")
    .trim()
    .toLowerCase();
  if (id === 3 || n.includes("complete")) return "status-badge status-badge--success";
  if (id === 4 || n.includes("cancel")) return "status-badge status-badge--danger";
  if (id === 2 || n.includes("progress")) return "status-badge status-badge--info";
  if (id === 1 || n.includes("pending")) return "status-badge status-badge--warning";
  return "status-badge";
}

function StatusBadge({ status, statusId }) {
  const label = status && String(status).trim() ? String(status).trim() : "—";
  return (
    <span className={trackerStatusBadgeClass(statusId, label)}>
      {label}
    </span>
  );
}

function formatShortDate(dateString) {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function trackerAttachmentHref(request) {
  const raw = request?.attachment;
  if (raw == null || String(raw).trim() === "") return null;
  return String(raw).trim();
}

function TrackerAttachmentCell({ request }) {
  const href = trackerAttachmentHref(request);
  if (!href) return "—";
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title="View attachment"
      className="maintenance-directory-attachment-link"
      aria-label="View attachment"
    >
      <Paperclip size={16} aria-hidden />
    </a>
  );
}

export default function OfficeMaintenanceStatusTrackerSection({
  masters,
  rows,
  loading,
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  onViewRequest,
  activeStatusKey = "all",
  onStatusFilterAll,
  onStatusFilterChange,
}) {
  const assignedLabel = useCallback(
    (request) =>
      request.assigned_to_name ||
      masters?.users?.find((u) => String(u.id) === String(request.assigned_to))?.name ||
      "Unassigned",
    [masters],
  );

  const statusLabelItems = useMemo(() => {
    const items = [{ key: "all", label: "All", variant: "all" }];
    const list = masters?.maintenanceStatuses || [];
    list.forEach((s, i) => {
      const name = String(s.status_name ?? "").trim();
      if (!name) return;
      items.push({
        key: String(s.id),
        label: name,
        variant: STATUS_PILL_VARIANT_CYCLE[i % STATUS_PILL_VARIANT_CYCLE.length],
      });
    });
    return items;
  }, [masters]);

  const isEmpty = !loading && rows.length === 0;

  return (
    <div id="mr-tracker" className="mr-tracker-embedded">
      <p className="mr-tracker-kicker">
        Same data as the directory, optimized for reading status and schedule fields. Filter by
        status below, or use <strong>View</strong> for full details.
      </p>

      <GlobalLabelFilters
        className="mr-tracker-label-filters"
        ariaLabel="Filter maintenance requests by status"
        items={statusLabelItems}
        activeKey={activeStatusKey}
        onAll={onStatusFilterAll}
        onToggle={onStatusFilterChange}
      />

      <GlobalDirectoryGridSection
        loading={Boolean(loading && rows.length === 0)}
        loadingMessage="Loading maintenance requests…"
        headers={TRACKER_HEADERS}
        isEmpty={isEmpty}
        emptyMessage="No maintenance requests found."
        tableAriaLabel="Maintenance status tracker"
        wrapClassName="ub-table-wrap user-builder-table-wrap"
        tableClassName="ub-table"
      >
        {rows.map((request) => (
          <tr key={request.id}>
            <td>{request.type}</td>
            <td>{request.asset_area}</td>
            <td>{assignedLabel(request)}</td>
            <td>
              <StatusBadge status={request.status} statusId={request.status_id} />
            </td>
            <td>{formatShortDate(request.start_date)}</td>
            <td>{formatShortDate(request.completion_date)}</td>
            <td>
              <TrackerAttachmentCell request={request} />
            </td>
            <td className="ub-table__col-actions">
              <div className="ub-table-actions">
                <GlobalTableActionButton
                  action="view"
                  onClick={() => onViewRequest?.(request)}
                  title="View request details"
                />
              </div>
            </td>
          </tr>
        ))}
      </GlobalDirectoryGridSection>

      {total > 0 ? (
        <div className="mr-tracker-pagination">
          <GlobalDirectoryPaginationSection
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={onPageChange}
            label="Maintenance status tracker pagination"
          />
        </div>
      ) : null}
    </div>
  );
}
